import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import "./melly/melly.css";

import { I18nProvider, useI18n } from "./melly/i18n";
import { AuthProvider, useAuth } from "./melly/auth";
import { AppStateProvider } from "./melly/state";
import { colors, fonts } from "./melly/theme";
import { Loading } from "./melly/ui";
import { Icon } from "./melly/Icon";

import Welcome from "./melly/screens/Welcome";
import Login from "./melly/screens/Login";
import Home from "./melly/screens/Home";
import Learn from "./melly/screens/Learn";
import Practice from "./melly/screens/Practice";
import Exams from "./melly/screens/Exams";
import More from "./melly/screens/More";
import Lesson from "./melly/screens/Lesson";
import Module from "./melly/screens/Module";
import Session from "./melly/screens/Session";
import Flashcards from "./melly/screens/Flashcards";
import Match from "./melly/screens/Match";
import Order from "./melly/screens/Order";
import Scenarios from "./melly/screens/Scenarios";
import Scenario from "./melly/screens/Scenario";
import Mistakes from "./melly/screens/Mistakes";
import Weak from "./melly/screens/Weak";
import Notes from "./melly/screens/Notes";
import Bookmarks from "./melly/screens/Bookmarks";
import Progress from "./melly/screens/Progress";
import Profile from "./melly/screens/Profile";
import ExamReview from "./melly/screens/ExamReview";
import Admin from "./melly/screens/Admin";
import AdminUsers from "./melly/screens/AdminUsers";
import AdminLearner from "./melly/screens/AdminLearner";

const TABS = [
  { path: "/home", icon: "house", key: "tabHome" },
  { path: "/learn", icon: "book-open", key: "tabLearn" },
  { path: "/practice", icon: "barbell", key: "tabPractice" },
  { path: "/exams", icon: "cap", key: "tabExams" },
  { path: "/more", icon: "dots-three", key: "tabMore" },
];

function BottomTabBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { tr } = useI18n();
  return (
    <div className="melly-tabbar">
      {TABS.map((t) => {
        const active = location.pathname === t.path;
        return (
          <div
            key={t.path}
            data-testid={`tab-${t.key}`}
            onClick={() => navigate(t.path)}
            style={{
              flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
              justifyContent: "center", gap: 3, paddingTop: 6, cursor: "pointer",
            }}
          >
            <Icon name={t.icon} size={24} color={active ? colors.brandPrimary : colors.muted} weight={active ? "fill" : "regular"} />
            <span style={{ fontSize: 11, fontWeight: fonts.medium, color: active ? colors.brandPrimary : colors.muted }}>{tr(t.key)}</span>
          </div>
        );
      })}
    </div>
  );
}

function Tabbed({ children }) {
  return (
    <div className="melly-fade" style={{ paddingBottom: 64 }}>
      {children}
      <BottomTabBar />
    </div>
  );
}

function Gate() {
  const { user, loading } = useAuth();
  const { ready } = useI18n();
  if (loading || !ready) return <Loading />;
  return <Navigate to={user ? "/home" : "/welcome"} replace />;
}

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/welcome" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Gate />} />
      <Route path="/welcome" element={<Welcome />} />
      <Route path="/login" element={<Login />} />

      <Route path="/home" element={<RequireAuth><Tabbed><Home /></Tabbed></RequireAuth>} />
      <Route path="/learn" element={<RequireAuth><Tabbed><Learn /></Tabbed></RequireAuth>} />
      <Route path="/practice" element={<RequireAuth><Tabbed><Practice /></Tabbed></RequireAuth>} />
      <Route path="/exams" element={<RequireAuth><Tabbed><Exams /></Tabbed></RequireAuth>} />
      <Route path="/more" element={<RequireAuth><Tabbed><More /></Tabbed></RequireAuth>} />

      <Route path="/lesson/:id" element={<RequireAuth><Lesson /></RequireAuth>} />
      <Route path="/module/:id" element={<RequireAuth><Module /></RequireAuth>} />
      <Route path="/session" element={<RequireAuth><Session /></RequireAuth>} />
      <Route path="/flashcards" element={<RequireAuth><Flashcards /></RequireAuth>} />
      <Route path="/match" element={<RequireAuth><Match /></RequireAuth>} />
      <Route path="/order" element={<RequireAuth><Order /></RequireAuth>} />
      <Route path="/scenarios" element={<RequireAuth><Scenarios /></RequireAuth>} />
      <Route path="/scenario/:id" element={<RequireAuth><Scenario /></RequireAuth>} />
      <Route path="/mistakes" element={<RequireAuth><Mistakes /></RequireAuth>} />
      <Route path="/weak" element={<RequireAuth><Weak /></RequireAuth>} />
      <Route path="/notes" element={<RequireAuth><Notes /></RequireAuth>} />
      <Route path="/bookmarks" element={<RequireAuth><Bookmarks /></RequireAuth>} />
      <Route path="/progress" element={<RequireAuth><Progress /></RequireAuth>} />
      <Route path="/profile" element={<RequireAuth><Profile /></RequireAuth>} />
      <Route path="/exam-review/:i" element={<RequireAuth><ExamReview /></RequireAuth>} />
      <Route path="/admin" element={<RequireAuth><Admin /></RequireAuth>} />
      <Route path="/admin/users" element={<RequireAuth><AdminUsers /></RequireAuth>} />
      <Route path="/admin/learner/:id" element={<RequireAuth><AdminLearner /></RequireAuth>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <AppStateProvider>
          <div className="melly-app">
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
          </div>
        </AppStateProvider>
      </AuthProvider>
    </I18nProvider>
  );
}
